import { PayloadAction } from "@reduxjs/toolkit";
import { AirFilterType, SeaFilterType } from "features/shared/filter.types";
import { useAirQueryKey } from "features/shared/useAirQueryKey";
import { usePanelViews } from "features/shared/usePanelViews.hook";
import {
  AirSavedCardModel,
  CountByAirStatusModel,
  isAirSavedCardModel,
  useAirDeleteCardMutation,
  useAirInfiniteScrollFetch,
} from "entities/AirSavedCard";
import { AirSearchCardModel } from "entities/AirSearchCard";
import { CountBySeaStatusModel, SeaSavedCardModel } from "entities/SeaSavedCard";
import { useAppDispatch } from "shared/lib/redux";

type UseAirDeleteAllCardsActionParams = {
  filter: SeaFilterType | AirFilterType;
  isAirFilter?: (filter: SeaFilterType | AirFilterType) => filter is AirFilterType;
  filteredCards: (SeaSavedCardModel | AirSavedCardModel)[];
  airSavedCards: AirSavedCardModel[];
  airSearchCards: AirSearchCardModel[];
  cardsByTags: (SeaSavedCardModel | AirSavedCardModel)[];
  closeDeleteAllCardsModal: () => void;
  setAllStatuses: (statuses: CountBySeaStatusModel[] | CountByAirStatusModel[]) => void;
  actions: {
    appendFilteredCardsAction: (payload: {
      data: (SeaSavedCardModel | AirSavedCardModel)[];
      unique?: boolean;
    }) => PayloadAction<{ data: (SeaSavedCardModel | AirSavedCardModel)[]; unique?: boolean }>;
    appendSavedCardsAction: (payload: {
      data: AirSavedCardModel[];
      unique?: boolean;
    }) => PayloadAction<{ data: AirSavedCardModel[]; unique?: boolean }>;
    removeFilteredCardAction: (
      cards: SeaSavedCardModel | AirSavedCardModel
    ) => PayloadAction<SeaSavedCardModel | AirSavedCardModel>;
    removeCardByTagAction: (
      cards: SeaSavedCardModel | AirSavedCardModel
    ) => PayloadAction<SeaSavedCardModel | AirSavedCardModel>;
    removeSavedCardAction: (cards: AirSavedCardModel) => PayloadAction<AirSavedCardModel>;
    removeSearchCardAction: (cards: AirSearchCardModel) => PayloadAction<AirSearchCardModel>;
    refreshTags: () => void;
  };
};

type UseAirDeleteAllCardsActionReturnType = {
  deleteAllAirCards: () => void;
};

export const useAirDeleteAllCardsAction = ({
  filteredCards,
  airSavedCards,
  airSearchCards,
  closeDeleteAllCardsModal,
  actions,
  isAirFilter,
  filter,
  setAllStatuses,
  cardsByTags,
}: UseAirDeleteAllCardsActionParams): UseAirDeleteAllCardsActionReturnType => {
  const dispatch = useAppDispatch();
  const {
    appendFilteredCardsAction,
    appendSavedCardsAction,
    removeFilteredCardAction,
    removeCardByTagAction,
    removeSavedCardAction,
    removeSearchCardAction,
    refreshTags,
  } = actions;
  const { isCurrentViewFilteredCards, isCurrentViewMain, showMainView, isCurrentViewCardsByTag } = usePanelViews();
  const { deleteCard } = useAirDeleteCardMutation();
  const { queryKey } = useAirQueryKey({
    isCurrentViewFilteredCards,
    isCurrentViewMain,
  });

  const { resetAirCardsQuery: resetCardsQuery, setRequestFilterSync: setRequestFilter } = useAirInfiniteScrollFetch({
    onBeforeRequest: () => {
      if (isAirFilter?.(filter)) setRequestFilter({ where: filter.where, order: filter.order });
    },
    queryKey: [queryKey],
    shouldRequestOnMount: false,
    onSuccess: ({ page, countByStatus }) => {
      if (!countByStatus.length) {
        setAllStatuses([]);
      }

      if (!page.length) {
        showMainView();
        return;
      }
      if (isCurrentViewFilteredCards) {
        dispatch(appendFilteredCardsAction({ data: page }));
      }
      if (isCurrentViewMain) {
        dispatch(appendSavedCardsAction({ data: page }));
      }
    },
  });

  const deleteSearchCards = (cards: AirSearchCardModel[]) => {
    cards.forEach((card) => {
      dispatch(removeSearchCardAction(card));
    });
  };

  const deleteCards = async (cards: AirSavedCardModel[]) => {
    const promises = cards.map((card) => {
      return deleteCard(card.id);
    });

    await Promise.all(promises)
      .then(() => {
        cards.forEach((card) => {
          dispatch(removeFilteredCardAction(card));
          dispatch(removeSavedCardAction(card));
          dispatch(removeCardByTagAction(card));
        });
      })
      .finally(() => {
        resetCardsQuery();
        closeDeleteAllCardsModal();
        refreshTags();
      });
  };

  const deleteAllAirCards = async () => {
    try {
      if (isCurrentViewFilteredCards) {
        await deleteCards(filteredCards.filter((card) => isAirSavedCardModel(card)));
      }
      if (isCurrentViewMain) {
        await deleteCards(airSavedCards);
        deleteSearchCards(airSearchCards);
      }
      if (isCurrentViewCardsByTag) {
        await deleteCards(cardsByTags.filter((card) => isAirSavedCardModel(card)));
      }
    } catch (_) {
      closeDeleteAllCardsModal();
    }
  };

  return { deleteAllAirCards };
};
